/*
 * Carton-fill progress + volume-discount guidance for Boxes and Ecobags.
 *
 * This is a display/guidance layer only. It never computes what a customer
 * is charged — all totals shown for items already in the cart are read
 * directly from /cart.js (which reflects Appstle's live discount). The only
 * arithmetic this file does itself is a forward-looking "if you add N more"
 * projection on the product page, using the tier table below.
 *
 * Physical carton capacity (from AFM): 12 or 24 Boxes per carton,
 * 3 or 8 Ecobags per carton.
 *
 * Tier tables below were read directly from the live Appstle Bundles
 * "Box Volume Pricing" and "Premium Box Volume Pricing" (Ecobag) automatic
 * discounts on 2026-09-13, and verified empirically against real /cart.js
 * responses (4 boxes -> 20% off, 12 boxes -> 30% off, 3 ecobags -> 10% off,
 * 8 ecobags -> 13% off, mixed flavours included). If AFM changes those
 * discount tiers in Appstle, these tables must be updated to match --
 * this file is not the source of truth, Appstle is.
 *
 * IMPORTANT, discovered during QA: when a cart contains BOTH Box and Ecobag
 * items at once, Appstle currently only applies ONE of the two automatic
 * discounts (Box wins; the Ecobag discount silently drops to 0%). This is a
 * pre-existing Appstle/discount-configuration behaviour, not something this
 * file can fix. Because of that, the tier table is used ONLY to describe
 * what a quantity SHOULD unlock (for "add N more" projections) -- the
 * "X% off unlocked" line always reads the REAL discount from /cart.js's own
 * line prices, and if the two disagree this widget says so explicitly
 * rather than silently promising a saving the customer won't get.
 */
(function () {
  'use strict';

  var BOX_VARIANTS = {
    41882011861088: { flavour: 'Original', price: 26.9 },
    41793868529760: { flavour: 'Chocolate', price: 26.9 },
    41793868562528: { flavour: 'Oat', price: 26.9 },
    41793868234848: { flavour: 'Avocado', price: 29.9 },
    41881995051104: { flavour: 'Purple Sweet Potato', price: 29.9 }
  };

  var ECOBAG_VARIANTS = {
    41793868431456: { flavour: 'Original', price: 75.0 },
    41793868398688: { flavour: 'Chocolate', price: 75.0 },
    41793868365920: { flavour: 'Oat', price: 75.0 },
    41793869086816: { flavour: 'Avocado', price: 80.0 },
    41793868464224: { flavour: 'Purple Sweet Potato', price: 80.0 }
  };

  // Ordered ascending; each tier's discount applies from its qty up to the next tier's qty.
  var BOX_TIERS = [
    { qty: 1, discount: 0 },
    { qty: 4, discount: 20 },
    { qty: 12, discount: 30 },
    { qty: 24, discount: 36 }
  ];

  var ECOBAG_TIERS = [
    { qty: 1, discount: 0 },
    { qty: 2, discount: 7 },
    { qty: 3, discount: 10 },
    { qty: 8, discount: 13 }
  ];

  // Physical carton completions (subset of the discount tiers above).
  var CARTON_MILESTONES = {
    box: [12, 24],
    ecobag: [3, 8]
  };

  var CONFIG = {
    box: { variants: BOX_VARIANTS, tiers: BOX_TIERS, milestones: CARTON_MILESTONES.box, unitLabel: 'Box', unitLabelPlural: 'Boxes' },
    ecobag: { variants: ECOBAG_VARIANTS, tiers: ECOBAG_TIERS, milestones: CARTON_MILESTONES.ecobag, unitLabel: 'Ecobag', unitLabelPlural: 'Ecobags' }
  };

  // Box flavours share only two base prices today (RM26.90 and RM29.90).
  // Grouped by price (not a fabricated "Signature/Premium" tier name) so the
  // ladder can show real per-box RM at each milestone for both price points.
  function priceGroupsFor(type) {
    var variants = CONFIG[type].variants;
    var groups = {};
    Object.keys(variants).forEach(function (id) {
      var v = variants[id];
      var key = v.price.toFixed(2);
      if (!groups[key]) groups[key] = { price: v.price, flavours: [] };
      groups[key].flavours.push(v.flavour);
    });
    return Object.keys(groups)
      .map(function (k) { return groups[k]; })
      .sort(function (a, b) { return a.price - b.price; });
  }

  function money(amount) {
    return 'RM' + amount.toFixed(2);
  }

  function tierFor(type, qty) {
    var tiers = CONFIG[type].tiers;
    var current = tiers[0];
    for (var i = 0; i < tiers.length; i++) {
      if (qty >= tiers[i].qty) current = tiers[i];
    }
    return current;
  }

  function nextTier(type, qty) {
    var tiers = CONFIG[type].tiers;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].qty > qty) return tiers[i];
    }
    return null;
  }

  function nextMilestone(type, qty) {
    var milestones = CONFIG[type].milestones;
    for (var i = 0; i < milestones.length; i++) {
      if (milestones[i] > qty) return milestones[i];
    }
    return null;
  }

  // Reads real quantities AND real applied discount from /cart.js line prices,
  // rather than assuming the tier table applies. Appstle only lets one of its
  // bundle discounts win when a cart qualifies for more than one at once (e.g.
  // Box + Ecobag together), so the tier table alone cannot be trusted to say
  // what's actually been charged -- only the cart's own prices can.
  function quantityInCart(type, cart) {
    var variants = CONFIG[type].variants;
    var byFlavour = {};
    var total = 0;
    var originalTotal = 0;
    var finalTotal = 0;
    if (!cart || !cart.items) {
      return { total: total, byFlavour: byFlavour, actualDiscountPct: 0 };
    }
    cart.items.forEach(function (item) {
      var meta = variants[item.variant_id];
      if (!meta) return;
      total += item.quantity;
      byFlavour[meta.flavour] = (byFlavour[meta.flavour] || 0) + item.quantity;
      originalTotal += item.original_line_price;
      finalTotal += item.final_line_price;
    });
    var actualDiscountPct = originalTotal > 0 ? Math.round((1 - finalTotal / originalTotal) * 1000) / 10 : 0;
    return { total: total, byFlavour: byFlavour, actualDiscountPct: actualDiscountPct };
  }

  function unitFor(type, count) {
    return count === 1 ? CONFIG[type].unitLabel.toLowerCase() : CONFIG[type].unitLabelPlural.toLowerCase();
  }

  // Returns { text, level } where level is 'subtle' | 'prominent' | 'complete'.
  function messageFor(type, qty) {
    if (type === 'box') {
      if (qty === 0) return null;
      if (qty >= 24) return { text: 'LARGE CARTON COMPLETE ✓', level: 'complete' };
      if (qty === 12) return { text: 'CARTON COMPLETE ✓', level: 'complete' };
      if (qty >= 18) return { text: (24 - qty) + ' more ' + unitFor(type, 24 - qty) + ' completes a Large Carton.', level: 'prominent' };
      if (qty >= 13) return { text: 'Building toward your next carton — ' + (24 - qty) + ' more ' + unitFor(type, 24 - qty) + ' to go.', level: 'subtle' };
      if (qty >= 8) return { text: "You're only " + (12 - qty) + ' ' + unitFor(type, 12 - qty) + ' away from completing a carton.', level: 'prominent' };
      if (qty >= 1) return { text: 'Building toward your carton — ' + (12 - qty) + ' more ' + unitFor(type, 12 - qty) + ' to go.', level: 'subtle' };
      return null;
    }
    // ecobag
    if (qty === 0) return null;
    if (qty >= 8) return { text: 'LARGE CARTON COMPLETE ✓', level: 'complete' };
    if (qty === 7) return { text: 'Just 1 more Ecobag to complete your Large Carton.', level: 'prominent' };
    if (qty === 6) return { text: '2 more Ecobags to complete a Large Carton.', level: 'subtle' };
    if (qty >= 4) return { text: 'Building toward your next carton — ' + (8 - qty) + ' more to go.', level: 'subtle' };
    if (qty === 3) return { text: 'CARTON COMPLETE ✓', level: 'complete' };
    if (qty === 2) return { text: 'Just 1 more Ecobag completes your carton.', level: 'prominent' };
    if (qty === 1) return { text: 'Add 2 more to complete a Standard Carton.', level: 'subtle' };
    return null;
  }

  // Full savings ladder: every milestone tier at once, so the customer can
  // see the whole picture rather than only the tier they're currently on.
  // compact=true (cart/cart drawer) drops the per-flavour-group RM/box lines.
  function ladderHTML(type, qty, compact) {
    var cfg = CONFIG[type];
    var tiers = cfg.tiers.filter(function (t) { return t.qty > 1; }); // skip the qty:1/0% baseline
    var groups = type === 'box' ? priceGroupsFor(type) : [];
    var topTierQty = tiers[tiers.length - 1].qty;

    var rows = tiers
      .map(function (t) {
        var reached = qty >= t.qty;
        var isTop = t.qty === topTierQty;
        var statusLabel = reached ? (isTop && qty >= topTierQty ? 'BEST CARTON TIER ✓' : 'UNLOCKED ✓') : (t.qty - qty) + ' MORE TO GO';
        var rowClass = reached ? 'nbr-carton-progress__ladder-row--reached' : 'nbr-carton-progress__ladder-row--pending';

        var priceLines = '';
        if (!compact && groups.length > 0) {
          priceLines = groups
            .map(function (g) {
              return '<span class="nbr-carton-progress__ladder-price">'
                + g.flavours.join(' / ') + ': ' + money(g.price * (1 - t.discount / 100)) + ' / ' + cfg.unitLabel.toLowerCase()
                + '</span>';
            })
            .join('');
        }

        return '<li class="nbr-carton-progress__ladder-row ' + rowClass + '">'
          + '<span class="nbr-carton-progress__ladder-qty">' + t.qty + ' ' + cfg.unitLabelPlural.toUpperCase() + '</span>'
          + '<span class="nbr-carton-progress__ladder-pct">' + t.discount + '% OFF</span>'
          + '<span class="nbr-carton-progress__ladder-status">' + statusLabel + '</span>'
          + priceLines
          + '</li>';
      })
      .join('');

    return '<div class="nbr-carton-progress__ladder-wrap">'
      + '<p class="nbr-carton-progress__ladder-heading">Save more as you fill your carton</p>'
      + '<ul class="nbr-carton-progress__ladder" role="list">' + rows + '</ul>'
      + '</div>';
  }

  // Progress bar with tick marks at each real milestone, scaled to the top tier.
  function milestoneBarHTML(type, qty) {
    var cfg = CONFIG[type];
    var tiers = cfg.tiers.filter(function (t) { return t.qty > 1; });
    var topQty = tiers[tiers.length - 1].qty;
    var fillPct = Math.min(100, Math.round((qty / topQty) * 100));

    var ticks = tiers
      .map(function (t) {
        var leftPct = Math.min(100, Math.round((t.qty / topQty) * 100));
        var reached = qty >= t.qty;
        return '<span class="nbr-carton-progress__tick' + (reached ? ' nbr-carton-progress__tick--reached' : '') + '" style="left:' + leftPct + '%">'
          + '<span class="nbr-carton-progress__tick-dot"></span>'
          + '<span class="nbr-carton-progress__tick-label">' + t.qty + '</span>'
          + '</span>';
      })
      .join('');

    return '<div class="nbr-carton-progress__milestone-bar">'
      + '<div class="nbr-carton-progress__bar"><div class="nbr-carton-progress__bar-fill" style="width:' + fillPct + '%"></div></div>'
      + '<div class="nbr-carton-progress__ticks">' + ticks + '</div>'
      + '</div>';
  }

  function render(root, type, cart, context) {
    var data = quantityInCart(type, cart);
    var qty = data.total;
    var cfg = CONFIG[type];

    if (qty === 0) {
      root.innerHTML = '';
      root.hidden = true;
      return;
    }
    root.hidden = false;

    var tier = tierFor(type, qty);
    var next = nextTier(type, qty);
    var nextMs = nextMilestone(type, qty);
    var message = messageFor(type, qty);
    // The tier table says what SHOULD apply at this quantity; actualDiscountPct
    // is what /cart.js says IS applied. They can differ -- e.g. Appstle only
    // lets one of its automatic discounts win when a cart qualifies for both
    // the Box and Ecobag volume discounts at once. Never claim more than the
    // cart actually shows.
    var appliedPct = data.actualDiscountPct;
    var expectedPct = tier.discount;
    var discountMismatch = expectedPct > 0 && appliedPct < expectedPct - 0.5;

    var progressTarget = nextMs || cfg.milestones[cfg.milestones.length - 1];
    var pct = Math.min(100, Math.round((qty / progressTarget) * 100));

    var flavourLines = Object.keys(data.byFlavour)
      .map(function (f) { return f + ' × ' + data.byFlavour[f]; })
      .join(', ');

    var html = '';
    html += '<div class="nbr-carton-progress__row">';
    html += '<span class="nbr-carton-progress__label">YOUR CARTON</span>';
    html += '<span class="nbr-carton-progress__count">' + qty + ' / ' + progressTarget + ' ' + cfg.unitLabelPlural.toUpperCase() + '</span>';
    html += '</div>';
    if (type === 'box') {
      html += milestoneBarHTML(type, qty);
    } else {
      html += '<div class="nbr-carton-progress__bar"><div class="nbr-carton-progress__bar-fill" style="width:' + pct + '%"></div></div>';
    }

    if (message) {
      html += '<p class="nbr-carton-progress__message nbr-carton-progress__message--' + message.level + '">' + message.text + '</p>';
    }

    var focusPrice = parseFloat(root.getAttribute('data-focus-price'));
    if (discountMismatch) {
      html += '<p class="nbr-carton-progress__tier nbr-carton-progress__tier--notice">'
        + 'Your order currently qualifies for a ' + expectedPct + '% ' + cfg.unitLabel.toLowerCase()
        + ' discount, but only ' + appliedPct + '% is reflected in your cart right now'
        + (appliedPct > 0 ? '' : ' — no ' + cfg.unitLabel.toLowerCase() + ' discount is currently applied')
        + '. Please check your cart total before checkout.</p>';
    } else if (appliedPct > 0) {
      var perUnit = focusPrice ? ' (' + money(focusPrice * (1 - appliedPct / 100)) + ' / ' + cfg.unitLabel.toLowerCase() + ')' : '';
      html += '<p class="nbr-carton-progress__tier">' + appliedPct + '% off unlocked on every ' + cfg.unitLabel.toLowerCase() + ' in this order' + perUnit + '.</p>';
    } else if (next) {
      var nextPerUnit = focusPrice ? ' (' + money(focusPrice * (1 - next.discount / 100)) + ' / ' + cfg.unitLabel.toLowerCase() + ')' : '';
      html += '<p class="nbr-carton-progress__tier nbr-carton-progress__tier--muted">Add ' + (next.qty - qty) + ' more to unlock ' + next.discount + '% off' + nextPerUnit + '.</p>';
    }

    if (flavourLines) {
      html += '<p class="nbr-carton-progress__mix">Mix your favourites — ' + flavourLines + '</p>';
    }

    if (type === 'box') {
      html += ladderHTML(type, qty, context === 'cart');
      html += '<p class="nbr-carton-progress__mix-note">Mix any flavours — your total box quantity counts toward the same savings tier.</p>';
    }

    root.innerHTML = html;
  }

  if (!customElements.get('nbr-carton-progress')) {
    customElements.define(
      'nbr-carton-progress',
      class NbrCartonProgress extends HTMLElement {
        connectedCallback() {
          this.mode = this.getAttribute('mode') || 'cart';
          this.renderFromCart();
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this._unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.renderFromCart());
          }
        }

        disconnectedCallback() {
          if (this._unsubscribe) this._unsubscribe();
        }

        renderFromCart() {
          return fetch('/cart.js')
            .then((res) => res.json())
            .then((cart) => this.renderCart(cart))
            .catch(() => {});
        }

        renderCart(cart) {
          const boxMount = this.querySelector('[data-carton-type="box"]');
          const ecobagMount = this.querySelector('[data-carton-type="ecobag"]');
          if (boxMount) render(boxMount, 'box', cart, this.mode);
          if (ecobagMount) render(ecobagMount, 'ecobag', cart, this.mode);

          if (this.mode === 'box' && boxMount) {
            this.hidden = boxMount.hidden;
          } else if (this.mode === 'ecobag' && ecobagMount) {
            this.hidden = ecobagMount.hidden;
          } else if (this.mode === 'cart') {
            this.hidden = (!boxMount || boxMount.hidden) && (!ecobagMount || ecobagMount.hidden);
          }
        }
      }
    );
  }

  window.NBRCartonProgress = {
    money: money,
    tierFor: tierFor,
    nextTier: nextTier,
    nextMilestone: nextMilestone,
    quantityInCart: quantityInCart,
    CONFIG: CONFIG
  };
})();
